import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Film, Lock, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Card } from '../../components/atoms/Card';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const { updatePassword, isLoading, error, clearError } = useAuthStore();

  // Check if user has a valid recovery token in URL
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // If no session, user might have clicked the email link
      // Supabase should have set the session automatically
      if (!session) {
        // Check for error in URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const errorParam = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        
        if (errorParam) {
          setValidationError(errorDescription || 'Invalid or expired reset link');
        } else {
          // Wait a bit for Supabase to process the token
          setTimeout(async () => {
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (!retrySession) {
              setValidationError('Invalid or expired reset link. Please request a new one.');
            }
            setIsValidating(false);
          }, 1000);
          return;
        }
      }
      
      setIsValidating(false);
    };
    
    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return;
    }
    
    if (password.length < 6) {
      return;
    }

    clearError();
    await updatePassword(password);
    
    if (!error) {
      setIsSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  const passwordsMatch = password === confirmPassword && password !== '';
  const passwordValid = password.length >= 6;

  if (isValidating) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
              <Film size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-black tracking-tighter uppercase text-white">
              PRODUKCIJSKI <span className="text-blue-500">MASTER</span>
            </h1>
          </div>

          <Card variant="glass" padding="lg" radius="3xl" className="text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">Link Expired</h2>
            <p className="text-slate-400 text-sm mb-6">{validationError}</p>
            <Button onClick={() => navigate('/forgot-password')} variant="primary">
              Request New Link
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
            <Film size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tighter uppercase text-white">
            PRODUKCIJSKI <span className="text-blue-500">MASTER</span>
          </h1>
        </div>

        <Card variant="glass" padding="lg" radius="3xl">
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-white mb-1">Set New Password</h2>
            <p className="text-sm text-slate-400">
              Enter your new password below
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {isSuccess ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <h3 className="text-white font-bold mb-2">Password Updated!</h3>
              <p className="text-slate-400 text-sm mb-4">
                Your password has been successfully changed.
              </p>
              <p className="text-slate-500 text-xs">
                Redirecting to login...{' '}
                <button 
                  onClick={() => navigate('/login')} 
                  className="text-blue-500 hover:underline"
                >
                  Go now
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftIcon={<Lock size={16} />}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              
              {/* Password requirements hint */}
              <p className="text-xs text-slate-500 -mt-2">
                Must be at least 6 characters
              </p>

              {/* Confirm Password */}
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  leftIcon={<Lock size={16} />}
                  required
                  minLength={6}
                  className={confirmPassword && !passwordsMatch ? 'border-red-500/50' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* Password match error */}
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-400 -mt-2">Passwords do not match</p>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isLoading}
                disabled={!passwordValid || !passwordsMatch}
              >
                Update Password
              </Button>
            </form>
          )}
        </Card>

        <p className="text-center mt-6 text-xs text-slate-600">
          © 2024 ZADAR ARCHIPELAGO PRODUCTIONS
        </p>
      </div>
    </div>
  );
}
